<template>
  <v-container class="bordered bg-white mb-4">
    <DataTable
      :headers="headers"
      :items="setupSteps" />
  </v-container>

  <v-container>
    <v-row class="ml-n4 mb-4">
      <v-col
        class="bordered bg-white"
        cols="6">
        <v-table class="bottom_table">
          <tbody>
            <tr>
              <td class="wide_col">
                {{
                  $t(
                    "entities.manufacturingStep.fields.labour_setup_costs_manufacturing_step_part"
                  )
                }}
              </td>
              <td class="input_col">
                <CalcOutput
                  :id="nodeId"
                  field="labour_setup_costs_manufacturing_step_part"
                  hide-label
                  width="80" />
              </td>
              <td class="e_per_piece_col">
                {{ $t("units.e_per_piece_long") }}
              </td>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
      <v-col cols="6"> &nbsp; </v-col>
    </v-row>

    <v-row class="ml-n4 mb-4">
      <v-col
        class="bordered bg-white"
        cols="6">
        <v-table class="bottom_table">
          <tbody>
            <tr>
              <td class="wide_col">
                {{
                  $t(
                    "entities.manufacturingStep.fields.machine_setup_costs_manufacturing_step_part"
                  )
                }}
              </td>
              <td class="input_col">
                <CalcOutput
                  :id="nodeId"
                  field="machine_setup_costs_manufacturing_step_part"
                  hide-label
                  width="80" />
              </td>
              <td class="e_per_piece_col">
                {{ $t("units.e_per_piece_long") }}
              </td>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
      <v-col cols="6"> &nbsp; </v-col>
    </v-row>

    <v-row class="ml-n4">
      <v-col
        class="bordered bg-white"
        cols="6">
        <v-table class="bottom_table">
          <tbody>
            <tr>
              <td class="wide_col">
                {{
                  $t(
                    "entities.manufacturingStep.fields.setup_costs_manufacturing_step_part_long"
                  )
                }}
              </td>
              <td class="input_col">
                <CalcOutput
                  :id="nodeId"
                  field="setup_costs_manufacturing_step_part"
                  hide-label
                  width="80" />
              </td>
              <td class="e_per_piece_col">
                {{ $t("units.e_per_piece_long") }}
              </td>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
      <v-col cols="6"> &nbsp; </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { numericFieldInTable, stringFieldInTable } from "@/utils/helper";
import { useSpaceStore } from "../../../../stores/spaceStore";
import CalcOutput from "../../../../components/Elements/CalcOutput.vue";
import DataTable from "@/components/Elements/DataTable.vue";

const spaceStore = useSpaceStore();

const { t } = useTranslation();
const step = ref(null);

const props = defineProps({
  nodeId: {
    type: String,
    required: true
  }
});

const part = computed(() => {
  return spaceStore.getElement(props.nodeId);
});

const setupSteps = computed(() => {
  const output = part.value.output_quantity_part || 0;

  return spaceStore
    .getElementList(props.nodeId, "machine")
    .reduce((result, machine) => {
      return [
        ...result,
        ...spaceStore
          .getElementList(machine.fullId, "setupStep")
          .reduce((tableSteps, tableStep) => {
            const labour_setup_cost =
              output === 0
                ? 0
                : (part.value.labour_hourly_rates_part *
                    Number(tableStep.setup_time_setupStep)) /
                  output;
            const machine_setup_cost =
              output === 0
                ? 0
                : (part.value.machine_hourly_rates_part *
                    Number(tableStep.setup_time_setupStep)) /
                  output;

            tableStep.sum_of_labour_hourly_rates_local =
              part.value.labour_hourly_rates_part;
            tableStep.sum_of_machine_hourly_rates_local =
              part.value.machine_hourly_rates_part;

            tableStep.labour_setup_costs_setup_step_part = labour_setup_cost;
            tableStep.machine_setup_costs_setup_step_part = machine_setup_cost;
            tableStep.setup_costs_setup_step_part =
              labour_setup_cost + machine_setup_cost;

            return [...tableSteps, tableStep];
          }, [])
      ];
    }, []);
});

onMounted(() => {
  step.value = spaceStore.calculationElements[props.nodeId];
});

const headers = computed(() => [
  stringFieldInTable({
    t,
    entity: "setupStep",
    field: "name"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "setup_time_setupStep"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "sum_of_labour_hourly_rates_local"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "sum_of_machine_hourly_rates_local"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "labour_setup_costs_setup_step_part"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "machine_setup_costs_setup_step_part"
  }),
  numericFieldInTable({
    t,
    entity: "manufacturingStep",
    field: "setup_costs_setup_step_part"
  })
]);
</script>

<style scoped lang="scss">
:deep() .v-data-table-header__content {
  font-weight: 700;
  font-size: smaller;
}

:deep()
  .border-table
  .v-table__wrapper
  > table
  > thead
  > tr
  > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep()
  .border-table
  .v-table__wrapper
  > table
  > tbody
  > tr
  > td:not(:last-child),
.border-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.bottom_table {
  font-size: 85%;

  .wide_col {
    width: 280px;
  }

  .input_col {
    //width: 80px;
    padding: 0 6px !important;

    .v-field__input {
      min-height: 10px !important;
    }
  }

  .e_per_piece_col {
    width: 30px;
    padding: 0 6px !important;
    white-space: nowrap;
  }

  td,
  th {
    border-bottom: none !important;
  }

  .low-height {
    td,
    th {
      height: 40px !important;
    }
  }

  .v-field__input {
    padding: 4px 8px !important;
  }
}
</style>
